import MagazineItem from "../components/MagazineItem";
import "./PageList.css";
import {ApiResultOfint, PageControllerApiFactory, PageItem} from "../api";
import {InfiniteData, QueryClient, useInfiniteQuery, useQueryClient} from "@tanstack/react-query";
import {fetchPageItems} from "../apiFacade/pageListApi";
import {useInView} from "react-intersection-observer";
import React, {ReactElement, useEffect, useState, useRef, useCallback, useMemo} from "react";
import {Button} from "@mui/material";
import Loading from "./Loading";
import {NavLabel} from "./Navigation/shared/NavLabels";
import SubHeader, {ButtonOptions} from "./SubHeader";
import {PageOperateEvent, PageOperation} from "./PageOperationButtons";
import {PageQueryKey} from "../domain/pageQueryKey";
import {useSnackbar} from "notistack";
import {AxiosPromise, AxiosResponse} from "axios";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TransitionAlert from "./common/TransitionAlert";
import {isDeepEqual} from "../common/objectUtils";
import {setDocTitle} from "../common/docUtils";
import {useSearchParams} from "react-router-dom";
import {safeInt} from "../common/typeUtils";
import {ContentType, SORT_VALUE} from "../model";
import PageDetailModal from "./PageDetailModal";
import Alert from "@mui/material/Alert";
import { useGlobalSettings } from "../contexts/GlobalSettingsContext";

export type PageListFilter = {
  asc?: boolean,
  connectorId?: number,
  connectorType?: number,
  count?: number,
  firstRecordDate?: string,
  folderId?: number;
  lastRecordDate?: string,
  readLater?: boolean,
  saveStatus?: 'ARCHIVED' | 'NOT_SAVED' | 'SAVED',
  sort?: SORT_VALUE,
  contentType?: ContentType,
  sourceId?: number,
  starred?: boolean,
  markRead?: boolean,
  contentFilterType?: number,
  startDate?: string,
  endDate?: string,
  hasHighlights?: boolean,
  collectionId?: number,
  filterUnsorted?: boolean,
  includeArchived?: boolean,
}

interface PageListProps {
  filters?: PageListFilter
  navLabel?: NavLabel,
  navLabelArea?: ReactElement,
  onMarkAllAsRead?: () => AxiosPromise<ApiResultOfint>,
  buttonOptions?: ButtonOptions,
  showMarkReadOption?: boolean,
  hasMarkReadOnScrollFeature?: boolean,
  filterComponent?: React.ReactElement,
  defaultSearchKeywords?: string[],
  defaultSearchText?: string
}

const PageList = (props: PageListProps) => {
  // ============ Props and Hooks ============
  const {
    buttonOptions,
    navLabel,
    navLabelArea,
    onMarkAllAsRead,
    showMarkReadOption,
    hasMarkReadOnScrollFeature,
    defaultSearchKeywords,
    defaultSearchText
  } = props;
  
  const {ref: inViewRef, inView} = useInView();
  const {enqueueSnackbar} = useSnackbar();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { markReadOnScroll } = useGlobalSettings();

  // ============ Basic State ============
  const selectedPageId = safeInt(params.get("p"));
  const propFilters = useMemo(() => props.filters || {}, [props.filters]);
  const [filters, setFilters] = useState<PageListFilter>(propFilters);
  const [showDoneTip, setShowDoneTip] = useState(false);
  const [lastVisitPageId, setLastVisitPageId] = useState(0);
  const pageSize = filters.count || 20;
  const queryKey = useMemo(() => [PageQueryKey.PageList, filters], [filters]);

  // ============ Scroll Tracking State and Logic ============
  const shouldEnableScrollTracking = hasMarkReadOnScrollFeature && markReadOnScroll;
  const [processedPages, setProcessedPages] = useState<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const pageListRef = useRef<HTMLDivElement | null>(null);
  const observedElementsRef = useRef<Set<Element>>(new Set());

  // Clean up observed elements that are no longer in DOM
  const cleanupObservedElements = useCallback(() => {
    if (!pageListRef.current) return;
    
    const currentElements = new Set(pageListRef.current.querySelectorAll('[data-page-id]'));
    const elementsToRemove: Element[] = [];
    
    observedElementsRef.current.forEach((element) => {
      if (!currentElements.has(element) || !element.isConnected) {
        elementsToRemove.push(element);
      }
    });
    
    elementsToRemove.forEach((element) => {
      if (observerRef.current) {
        observerRef.current.unobserve(element);
      }
      observedElementsRef.current.delete(element);
    });
  }, []);

  // Observe all page items when data changes
  const observePageItems = useCallback(() => {
    if (!shouldEnableScrollTracking || !observerRef.current || !pageListRef.current) return;

    // Find all page items and observe only new ones
    const pageItems = pageListRef.current.querySelectorAll('[data-page-id]');
    pageItems.forEach((item) => {
      // Only observe if not already observed
      if (observerRef.current && !observedElementsRef.current.has(item)) {
        observerRef.current.observe(item);
        observedElementsRef.current.add(item);
      }
    });
  }, [shouldEnableScrollTracking]);

  // Mark pages as read via API
  const markPagesAsRead = useCallback((pageIds: number[]) => {
    if (pageIds.length === 0) return;

    PageControllerApiFactory().markReadByPageIdsUsingPOST(pageIds).then((res) => {
      if (res && res.data && res.data.code === 0) {
        // Update query data to reflect the change
        queryClient.setQueryData<InfiniteData<PageItem[]>>(queryKey, oldData => ({
          ...oldData,
          pages: oldData.pages.map(pages => pages.map((rawPage): PageItem => 
            pageIds.includes(rawPage.id) ? { ...rawPage, markRead: true } : rawPage
          ))
        }));
        
        // 数据更新后，重新初始化滚动观察器
        if (shouldEnableScrollTracking) {
          setTimeout(() => {
            cleanupObservedElements();
            observePageItems();
          }, 100);
        }
      }
    }).catch(err => {
      console.error('Failed to mark pages as read:', err);
    });
  }, [queryKey, queryClient, shouldEnableScrollTracking, cleanupObservedElements, observePageItems]);

  // Mark pages as read when they scroll past (become invisible from top)
  const markPageAsReadOnScroll = useCallback((pageId: number) => {
    // Skip if already processed
    if (processedPages.has(pageId)) return;
    
    // Check if page exists and is not already marked as read
    const queryData = queryClient.getQueryData<InfiniteData<PageItem[]>>(queryKey);
    const page = queryData?.pages?.flat().find(p => p.id === pageId);
    
    if (page && !page.markRead) {
      // Mark as processed to avoid duplicate calls
      setProcessedPages(prev => {
        const newProcessed = new Set(prev);
        newProcessed.add(pageId);
        return newProcessed;
      });
      
      // Call API to mark as read
      markPagesAsRead([pageId]);
    }
  }, [processedPages, queryClient, queryKey, markPagesAsRead]);

  // Initialize Intersection Observer for scroll tracking
  useEffect(() => {
    if (!shouldEnableScrollTracking) return;

    const observedElements = observedElementsRef.current;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const pageId = parseInt(entry.target.getAttribute('data-page-id') || '0');
          if (pageId === 0) return;

          // When element scrolls out of view going downward (scrolled past), mark as read
          // This is the simple "mark read when scrolling down past them" behavior
          if (!entry.isIntersecting && entry.boundingClientRect.bottom < entry.rootBounds.top) {
            markPageAsReadOnScroll(pageId);
          }
        });
      },
      {
        rootMargin: '-90px 0px 0px 0px',
        threshold: 0
      }
    );

    return () => {
      const currentObserver = observerRef.current;
      if (currentObserver) {
        currentObserver.disconnect();
        observedElements.clear();
      }
    };
  }, [shouldEnableScrollTracking, markPageAsReadOnScroll]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // ============ Data Fetching ============
  const {
    isLoading,
    data,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useInfiniteQuery(
    queryKey,
    async ({
             pageParam = {
               lastRecordAt: undefined,
               firstRecordAt: undefined,
               firstVoteScore: undefined,
               lastVoteScore: undefined
             }
           }) => {
      return await fetchPageItems({
        asc: filters.asc,
        collectionId: filters.collectionId,
        connectorId: filters.connectorId,
        connectorType: filters.connectorType,
        contentFilterType: filters.contentFilterType,
        contentType: filters.contentType,
        count: pageSize,
        endDate: filters.endDate,
        filterUnsorted: filters.filterUnsorted,
        firstRecordAt: pageParam.firstRecordAt || undefined,
        firstVoteScore: pageParam.firstVoteScore || undefined,
        folderId: filters.folderId,
        hasHighlights: filters.hasHighlights,
        includeArchived: filters.includeArchived,
        lastRecordAt: pageParam.lastRecordAt || undefined,
        lastVoteScore: pageParam.lastVoteScore || undefined,
        markRead: filters.markRead,
        readLater: filters.readLater,
        saveStatus: filters.saveStatus,
        sort: filters.sort,
        sourceId: filters.sourceId,
        starred: filters.starred,
        startDate: filters.startDate
      });
    },
    {
      getPreviousPageParam: (firstPage) =>
        firstPage && firstPage.length > 0
          ? (filters.sort === 'VOTE_SCORE' ? {firstVoteScore: firstPage[0].voteScore} : {firstRecordAt: firstPage[0].recordAt})
          : {},
      getNextPageParam: (lastPage) =>
        lastPage && lastPage.length > 0 && lastPage.length >= pageSize
          ? (filters.sort === 'VOTE_SCORE' ? {lastVoteScore: lastPage[lastPage.length - 1].voteScore} : {lastRecordAt: lastPage[lastPage.length - 1].recordAt})
          : undefined,
      keepPreviousData: false,
      refetchOnMount: true,
    },
  );

  // Re-initialize scroll tracking when data changes or modal state changes
  useEffect(() => {
    if (!shouldEnableScrollTracking) return;

    // Skip if no data is available yet
    if (!data || !data.pages || data.pages.length === 0) return;

    // Use a longer delay to ensure DOM updates are complete
    // This handles both data loading and modal state changes
    const timeoutId = setTimeout(() => {
      cleanupObservedElements();
      observePageItems();
    }, 600); // Single timeout that handles both scenarios

    return () => clearTimeout(timeoutId);
  }, [data, selectedPageId, shouldEnableScrollTracking, cleanupObservedElements, observePageItems]);

  // ============ Mark Read Operations ============
  function markListAsRead() {
    const queryData = queryClient.getQueryData<InfiniteData<PageItem[]>>(queryKey);
    if (queryData && queryData.pages) {
      const pageIds = [];
      queryData.pages.forEach(p => {
        p.forEach(page => {
          if (!page.markRead) {
            pageIds.push(page.id);
          }
        })
      });

      PageControllerApiFactory().markReadByPageIdsUsingPOST(pageIds).then((res) => {
        handleMarkReadResult(res);
      });
    }
  }

  function handleMarkReadResult(res: AxiosResponse<ApiResultOfint>) {
    if (res && res.data && res.data.code === 0) {
      enqueueSnackbar('Marked ' + res.data.data + ' pages as read.', {
        variant: "success",
        anchorOrigin: {vertical: "bottom", horizontal: "center"}
      });
      updateQueryDataMarkRead();
    }
  }

  function markAllAsRead() {
    onMarkAllAsRead().then(handleMarkReadResult);
  }

  function updateQueryDataMarkRead() {
    queryClient.setQueryData<InfiniteData<PageItem[]>>(queryKey, oldData => ({
      ...oldData,
      pages: oldData.pages.map(pages => pages.map((rawPage): PageItem => ({
        ...rawPage,
        markRead: true
      })))
    }));
    
    // 数据更新后，重新初始化滚动观察器
    if (shouldEnableScrollTracking) {
      setTimeout(() => {
        cleanupObservedElements();
        observePageItems();
      }, 100);
    }
  }

  // ============ Page Operations ============
  function operateSuccess(event: PageOperateEvent) {
    updatePageListQueryData(event, queryClient, queryKey);
    
    // 数据更新后，重新初始化滚动观察器
    if (shouldEnableScrollTracking) {
      setTimeout(() => {
        cleanupObservedElements();
        observePageItems();
      }, 100);
    }
  }

  function updatePageListQueryData(event: PageOperateEvent, queryClient: QueryClient, queryKey) {
    if (event.operation === PageOperation.markRead || event.operation === PageOperation.unMarkRead) {
      // Handle markRead/unMarkRead operations (they don't have result)
      queryClient.setQueryData<InfiniteData<PageItem[]>>(queryKey, oldData => ({
        ...oldData,
        pages: oldData.pages.map(pages => pages.map(rawPage => rawPage.id === event.rawPageStatus.id ? {
          ...rawPage,
          markRead: event.operation === PageOperation.markRead
        } : rawPage))
      }));
    } else if (event.operation !== PageOperation.delete) {
      const res = event.result;
      queryClient.setQueryData<InfiniteData<PageItem[]>>(queryKey, oldData => ({
        ...oldData,
        pages: oldData.pages.map(pages => pages.map(rawPage => rawPage.id === event.rawPageStatus.id ? {
          ...rawPage,
          starred: res.starred,
          readLater: res.readLater,
          librarySaveStatus: res.librarySaveStatus
        } : rawPage))
      }));
    } else {
      queryClient.setQueryData<InfiniteData<PageItem[]>>(queryKey, oldData => ({
        ...oldData,
        pages: oldData.pages.map(pages => pages.filter(curPage => curPage.id !== event.rawPageStatus.id))
      }));
      closePageDetail();
      enqueueSnackbar('Page deleted.', {
        variant: "success",
        anchorOrigin: {vertical: "bottom", horizontal: "center"}
      });
    }
  }

  // ============ Navigation and UI ============
  const isMobile = () => window.innerWidth <= 900;

  function closePageDetail() {
    setParams({}, {preventScrollReset: true});
  }

  function openPageDetail(e, pageId) {
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    // On mobile, let the Link navigate directly to the page detail
    if (isMobile()) {
      return;
    }
    e.preventDefault();
    setParams({p: pageId}, {preventScrollReset: true});
  }

  // ============ Effects ============
  // if there is no un read pages, then load all pages
  if (filters.markRead === false && data && data.pages && (data.pages.length === 0 || data.pages[0].length === 0)) {
    setShowDoneTip(true);
    setFilters({...filters, markRead: undefined});
  }

  useEffect(() => {
    if (!isDeepEqual(filters, propFilters)) {
      setShowDoneTip(false);
      setFilters(propFilters);
      // Reset processed pages when filters change
      setProcessedPages(new Set());
    }
  }, [propFilters, filters]);

  useEffect(() => {
    if (selectedPageId === 0) {
      setDocTitle(navLabel.labelText);
    } else {
      setLastVisitPageId(selectedPageId);
    }
  }, [selectedPageId, navLabel]);

  useEffect(() => {
    if (inView && !isLoading && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, isLoading, isFetchingNextPage, fetchNextPage]);

  // ============ Render ============
  return (
    <>
      <PageDetailModal selectedPageId={selectedPageId} operateSuccess={operateSuccess} onClose={closePageDetail}/>
       <SubHeader navLabel={navLabel} onMarkListAsRead={markListAsRead} onMarkAllAsRead={markAllAsRead}
                  navLabelArea={navLabelArea}
                  rightContent={props.filterComponent}
                  buttonOptions={buttonOptions}
                  defaultSearchKeywords={defaultSearchKeywords}
                  defaultSearchText={defaultSearchText}/>
       <div className={'flex flex-auto overflow-hidden'}>
        <div className="page-list-container p-2 flex flex-col grow items-center min-w-0 overflow-y-auto overflow-x-hidden">
          <div className={'page-list w-full max-w-[720px] flex flex-col items-center min-h-[400px]'} ref={pageListRef}>
            {showDoneTip && <div className={'w-full'}>
                <TransitionAlert severity="info">
                    You've read all articles in this section.
                </TransitionAlert>
                <div className="separator pt-2 pb-2"><ExpandMoreIcon/> Older articles</div>
            </div>}
            {isLoading && <Loading/>}
            {error && <p>Oops, something was broken. <div>{error.toString()}</div></p>}
            {!isLoading && !error && data &&
                <>
                  {
                    (data.pages.length === 0 || data.pages[0].length === 0) && <div className={'w-full'}>
                          <Alert severity="info">
                              No articles found in this section.
                          </Alert>
                      </div>
                  }
                  {data.pages.map((pages, index) =>
                    <React.Fragment key={index}>
                      {pages.map((page) => {
                          return <div 
                            className={`w-full`} 
                            key={page.id} 
                            data-page-id={!page.markRead ? page.id : undefined} // for scroll mark read feature
                          >
                            <MagazineItem page={page} showMarkReadOption={showMarkReadOption}
                                               onOperateSuccess={operateSuccess}
                                               currentVisit={lastVisitPageId === page.id}
                                               onPageSelect={(e, id) => openPageDetail(e, id)}
                                               pageListSort={filters.sort}></MagazineItem>
                          </div>;
                        }
                      )}
                    </React.Fragment>
                  )}
                    <div className={"mt-3 mb-3"}>
                      {isFetchingNextPage
                        ? <Loading/>
                        : hasNextPage
                          ? <Button variant="text" ref={inViewRef}>Load More</Button>
                          : <div></div>
                      }
                    </div>
                </>
            }
          </div>
        </div>
      </div>
    </>
  );
};

export default PageList;
